import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import {
  getTrackingIntervalDisplay,
  TrackingInterval,
} from '@/features/projects/create-subproject/types/tracking-interval';
import { NavigationButton } from '@/features/projects/project-details/components/navigation-button';
import { ProjectStatusIndicator } from '@/features/projects/projects-hub/components/project-status-indicator';
import { useProjectSubprojects } from '@/features/projects/shared/hooks/use-project-subprojects';
import { ProjectStatus } from '@/features/projects/shared/types/project-status';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { useProjectUsagePermissions } from '@/features/subscriptions/hooks/use-project-entitlements';
import PlusIcon from '@/shared/assets/plus.svg?react';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { Section } from '@/shared/components/section/section';
import {
  SectionActions,
  SectionHeader,
  SectionTitle,
} from '@/shared/components/section/section-header';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import { CustomTableRow } from '@/shared/components/table/table-row';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import { TableBody } from '@/shared/components/ui/table';
import { getInitials } from '@/shared/utils/avatar-util';

export const ProjectDetailsSubProjects = ({
  projectId,
}: {
  projectId: number;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const { t: subProjectTableT } = useTranslation('project', {
    keyPrefix: 'SubProjectTable',
  });
  const { t: trackingIntervalT } = useTranslation('subproject', {
    keyPrefix: 'TrackingInterval',
  });

  const {
    isLoading,
    data: subProjects,
    isError,
  } = useProjectSubprojects({
    projectId,
  });
  const { can_add_subproject } = useProjectUsagePermissions(projectId);
  const { openProjectPaywall } = usePaywall();

  function handleCreateSubproject() {
    if (!can_add_subproject) {
      openProjectPaywall(projectId);
      return;
    }
    navigate(paths.projects.subproject.create.getHref(projectId));
  }

  const columns = [
    subProjectTableT('title'),
    subProjectTableT('status'),
    subProjectTableT('partner'),
    subProjectTableT('trackingInterval'),
    subProjectTableT('notifications'),
    subProjectTableT('lastEntry'),
  ];

  const columnWidths = [
    'w-[257px]', // Project name
    'w-[256px]', // Status
    'w-[258px]', // Partner
    'w-[257px]', // Tracking interval
    'w-[256px]', // Notifications
    'flex-1', // Last entry
  ];

  const displayedSubProjects = subProjects?.slice(0, 3) ?? [];
  const totalSubProjectsCount = subProjects?.length ?? 0;

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('sub_projects')}</SectionTitle>
        <SectionActions>
          <Button
            variant={'greenGradient'}
            btnType="secondary"
            onClick={handleCreateSubproject}
          >
            <PlusIcon />
            {t('create_sub_project')}
          </Button>
          <NavigationButton
            onClick={() =>
              navigate(paths.projects.subproject.hub.getHref(projectId))
            }
          />
        </SectionActions>
      </SectionHeader>
      {isLoading ? (
        <LoadingIndicator />
      ) : isError || !subProjects ? (
        <div>{t('could_not_fetch_subprojects')}</div>
      ) : subProjects.length === 0 ? (
        <EmptyTablePlaceholder
          title={subProjectTableT('no_subprojects')}
          description={subProjectTableT('no_subprojects_description')}
          columns={columns}
          action={handleCreateSubproject}
        />
      ) : (
        <>
          <CustomTable>
            <SubprojectsTableHeader
              tableColumns={columns}
              columnWidths={columnWidths}
            />

            <TableBody>
              {displayedSubProjects.map((row, index) => {
                const contractor = row.contractors[0];
                const isInternal =
                  contractor &&
                  contractor.owner_orga_id != null &&
                  contractor.partner_orga_id != null &&
                  contractor.owner_orga_id === contractor.partner_orga_id;
                const partnerName = isInternal
                  ? subProjectTableT('internal_partner')
                  : contractor?.alias;

                return (
                  <CustomTableRow
                    key={row.id}
                    props={[
                      {
                        value: { type: 'string', text: row.name ?? '' },
                        isLeftMost: true,
                        width: columnWidths[0] || 'flex-1',
                      },
                      {
                        value: {
                          type: 'custom',
                          item: (
                            <ProjectStatusIndicator
                              status={row.status as ProjectStatus}
                            />
                          ),
                        },
                        width: columnWidths[1] || 'flex-1',
                      },
                      {
                        value: {
                          type: 'custom',
                          item: partnerName ? (
                            <div className="flex items-center gap-2">
                              <Avatar size="sm">
                                <AvatarFallback>
                                  {getInitials(partnerName)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-2xs font-normal">
                                {partnerName}
                              </span>
                            </div>
                          ) : (
                            <span className="text-2xs text-muted-foreground">
                              —
                            </span>
                          ),
                        },
                        width: columnWidths[2] || 'flex-1',
                      },
                      {
                        value: {
                          type: 'string',
                          text: row.tracking_interval
                            ? getTrackingIntervalDisplay({
                                interval:
                                  row.tracking_interval as TrackingInterval,
                                t: trackingIntervalT,
                              })
                            : '—',
                        },
                        width: columnWidths[3] || 'flex-1',
                      },
                      row.unread_notification_count > 0
                        ? {
                            value: {
                              type: 'number',
                              number: row.unread_notification_count,
                            },
                            width: columnWidths[4] || 'flex-1',
                          }
                        : {
                            value: {
                              type: 'string',
                              text: subProjectTableT('no_notifications'),
                            },
                            width: columnWidths[4] || 'flex-1',
                          },
                      row.last_entry_date
                        ? {
                            value: {
                              type: 'date',
                              date: new Date(row.last_entry_date),
                            },
                            isRightMost: true,
                            width: columnWidths[5] || 'flex-1',
                          }
                        : {
                            value: {
                              type: 'string',
                              text: subProjectTableT('no_entries'),
                            },
                            isRightMost: true,
                            width: columnWidths[5] || 'flex-1',
                          },
                    ]}
                    type="single"
                    isFirstRow={index === 0}
                    isLastRow={index === displayedSubProjects.length - 1}
                    onClick={() => {
                      navigate(
                        paths.projects.details.base.getHref(row.id.toString()),
                      );
                    }}
                  />
                );
              })}
            </TableBody>
          </CustomTable>
          {displayedSubProjects.length > 2 && (
            <div
              className="mt-2 size-fit cursor-pointer self-center rounded-full bg-custom-dark-grey px-3 py-1.5 text-2xs text-muted-foreground text-white"
              onClick={() => {
                navigate(paths.projects.subproject.hub.getHref(projectId));
              }}
            >
              {t('sub_projects_from_total', {
                count: displayedSubProjects.length,
                total: totalSubProjectsCount,
              })}
            </div>
          )}
        </>
      )}
    </Section>
  );
};

const SubprojectsTableHeader = ({
  tableColumns,
  columnWidths,
}: {
  tableColumns: string[];
  columnWidths: string[];
}) => {
  return (
    <CustomTableHeader
      columns={tableColumns}
      columnWidths={columnWidths}
      withContainer={false}
    />
  );
};
